import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import {
  useTranslation,
  ButtonTypes,
  ButtonStyles,
  ButtonWidth,
  ButtonSize,
  AccountType,
  NextgenImage,
  LoaderType,
} from '@nextgen-web-framework/all';
import {
  updateRegistrationData,
  profileApi,
  CreateChallengeRequest,
  CreateChallengeResponse,
  RegistrationResponse,
  updateCustomerType,
  RegFlowConfig,
} from '@commerce-profile-webcomponents/sdk';

import { ERROR_GENERIC_MESSAGE } from '../../utils/Constants';
import { APICallType, StepType } from '../Registration/types/propTypes';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import RegistrationOTPWrapper from '../RegistrationOTPWrapper/RegistrationOTPWrapper';
import { storeRegistrationInfoToSession } from '../../utils/registrationUtils';
import SkeletonLoader from '../Common/skeleton-loader/SkeletonLoader';
import ButtonWithLoading from '../Common/ButtonWithLoading/ButtonWithLoading';
import { RegistrationDownlineVerifyStep } from './types/propTypes';
import styles from './RegistrationDownlineVerifyStep.module.scss';

const RegistrationDownlineVerifyStep = ({ nextHandler }: RegistrationDownlineVerifyStep) => {
  const t = useTranslation();
  const globalErrorHandler = useGlobalErrorHandler();
  const dispatch = useDispatch<AppDispatch>();
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const personalDetailsImage = accountTypeImagesCmsData?.[selectedAccountType]?.personalDetailsImage;
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { OtpData, userData, currentIndex, flowName } = registrationFlowData;
  const flowSteps: StepType[] = useSelector((state: AppState) => state.commerceProfile.currentFlowSteps);

  const [isOtpDrawerOpen, setIsOtpDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [challengeId, setChallengeId] = useState<string>('');
  const [registerResponse, setRegisterResponse] = useState<RegistrationResponse>();
  const { registrationId } = userData;
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();

  const initHandler = async () => {
    const response = await profileApi.getRegistrationDetailsById(registrationId);
    dispatch(updateCustomerType(response?.accountType));
    setRegisterResponse(response);
  };

  useEffect(() => {
    initHandler();
  }, []);

  const image = {
    url: 'https://images.contentstack.io/v3/assets/blt62e43650fa9fd994/blt7485aa0494bde7b7/66c57cb21342863fb0c3e21c/T1.png',
    altText: '',
  };

  const initiateOtpHandler = async () => {
    const payload: CreateChallengeRequest = {};
    try {
      if (registrationId) {
        const challengeRes = await globalErrorHandler<CreateChallengeResponse>(() =>
          profileApi.postRegistrationChallenge({
            payload,
            registrationId,
          }),
        );
        setRegisterResponse((response) => ({ ...response, ...challengeRes }));
        setChallengeId(String(challengeRes?.challengeId));
        const updatedData = {
          ...registrationFlowData,
          OtpData: {
            ...OtpData,
            challengeId: challengeRes?.challengeId,
          },
          userData: {
            ...userData,
            jwtToken: '',
            patchVersion: challengeRes?.version,
          },
        };
        dispatch(updateRegistrationData(updatedData));
        setIsOtpDrawerOpen(true);
      }
    } catch (error) {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.headline,
      });
    }
  };

  const submitRegistrationdata = useCallback(async () => {
    setIsLoading(true);
    const nextStepNameConfig =
      selectedAccountType &&
      flowSteps?.[currentIndex].config?.[selectedAccountType]?.find((configData) => configData.key === 'nextStepName')
        ?.value;
    const nextStepIdTemp = flowSteps?.findIndex((flowStep) => flowStep.stepName === nextStepNameConfig);
    const nextStepId: number = nextStepIdTemp === -1 ? currentIndex + 1 : nextStepIdTemp;
    storeRegistrationInfoToSession(registrationId, challengeId, nextStepId);
    const response = await nextHandler(
      { version: Number(registerResponse?.version), otpRequestId: challengeId },
      undefined,
      false,
      false,
      APICallType.PATCH,
      'userDetailsStep',
    );
    setIsLoading(false);
    return response;
  }, [registerResponse?.version, challengeId, currentIndex]);

  return (
    <DesktopLayout desktopImage={personalDetailsImage} imageClassName={styles['desktop-image']}>
      <div className={styles['registration-verify-wrapper']} data-testid="registration-verify-wrapper">
        <div className={styles['registration-verify-conatiner']} data-testid="registration-verify-conatiner">
          {flowName === RegFlowConfig.CoApplicantFlow && registerResponse && (
            <div className={styles['registration-verify-heading']} data-testid="registration-verify-heading">
              {t(`profile.downlineRegistrationStep.${flowName}.heading`)}
            </div>
          )}
          {image ? (
            <div className={styles['registration-verify-image']}>
              <NextgenImage src={image.url} loaderType={LoaderType.CONTENTSTACK} alt={image.altText} fill={true} />
            </div>
          ) : (
            <SkeletonLoader />
          )}
          {registerResponse ? (
            <>
              {flowName === RegFlowConfig.AboLedFlow && (
                <div className={styles['registration-verify-heading']} data-testid="registration-verify-heading">
                  {t(`profile.downlineRegistrationStep.${flowName}.heading`)?.replace(
                    '{accountType}',
                    t(`profile.findoutMore.${registerResponse?.accountType}`),
                  )}{' '}
                  <span>{registerResponse?.sponsorName}</span>
                </div>
              )}
              {flowName === RegFlowConfig.AboLedFlow && (
                <div className={styles['registration-verify-content']} data-testid="registration-verify-content">
                  {t(`profile.downlineRegistrationStep.${flowName}.description`)}
                </div>
              )}

              {/* ---------verification-btn-------- */}
              <div className={styles['start-btn']}>
                <ButtonWithLoading
                  title={t('profile.common.nextButton')}
                  type={ButtonTypes.BUTTON}
                  data-testid="start-btn-testid"
                  buttonStyle={ButtonStyles.SOLID}
                  size={ButtonSize.MEDIUM}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  onClick={initiateOtpHandler}
                  disabled={isLoading}
                  isLoading={isLoading}
                />
              </div>
            </>
          ) : (
            <div className={styles['loader-div']}>
              <SkeletonLoader />
            </div>
          )}

          {isOtpDrawerOpen && (
            <RegistrationOTPWrapper
              nextHandler={() => {
                submitRegistrationdata();
              }}
              username={registerResponse?.phone ?? String(registerResponse?.email)}
              challengeId={challengeId}
              showChangeCTA={false}
              onCloseDrawer={() => {
                // disableFields(false);
                setIsOtpDrawerOpen(false);
              }}
              onUsernameChangeHandler={() => setIsOtpDrawerOpen(false)}
            />
          )}
        </div>
      </div>

      {(modalErrorData?.headline || modalErrorData?.body) && <ApiErrorModal {...modalErrorData} />}
    </DesktopLayout>
  );
};
export default RegistrationDownlineVerifyStep;
